import { createContext } from '@hover/blueprint';
import type { PropsWithChildren } from 'react';
import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import type { ObjectSchema } from 'yup';
import { number, object, string } from 'yup';

import { isBrowser, isServer } from 'src/shared/utils/environment';
import { escapeJSString } from 'src/shared/utils/sanitize';

import { useNonce } from '../useNonce';

export interface PostDataContext {
  fullName?: string;
  leadId?: number;
  email?: string;
}

interface PostDataProviderProps extends PropsWithChildren<{}> {
  data?: PostDataContext;
}

const postDataSchema: ObjectSchema<PostDataContext> = object({
  fullName: string().optional(),
  leadId: number().optional(),
  email: string().optional(),
});

const [Provider, usePostData] = createContext<PostDataContext>({
  strict: true,
  name: 'PostDataContext',
});
const safeParse = (data: string): PostDataContext => {
  try {
    return postDataSchema.validateSync(JSON.parse(data), {
      stripUnknown: true,
    });
  } catch {
    return {};
  }
};
const postDataWindowKey = '__postData__';
const PostDataProvider = ({ children, data }: PostDataProviderProps) => {
  const nonce = useNonce();
  let contextValue = data;

  if (isBrowser()) {
    contextValue = safeParse(
      window[postDataWindowKey as keyof Window] as string,
    );
  }

  useEffect(() => {
    // shouldn't happen since effects don't run on server
    if (isServer()) return;
    // clean up the data on the window object
    delete window[postDataWindowKey as keyof Window];
  }, []);

  // Use ref to keep value the same reference over re-renders
  const contextValueRef = useRef(contextValue ?? {});

  return (
    <>
      {isServer() && (
        <Helmet>
          <script nonce={nonce}>{`window[${JSON.stringify(
            postDataWindowKey,
          )}] = "${escapeJSString(JSON.stringify(data))}"`}</script>
        </Helmet>
      )}
      <Provider value={contextValueRef.current}>{children}</Provider>
    </>
  );
};

export { usePostData, PostDataProvider, postDataWindowKey };
